import {
  ActivityDefinition,
  DiagnosticReport,
  Extension,
  Observation,
  ObservationDefinition,
  ServiceRequest,
  ValueSet,
} from 'fhir/r4b';
import { evaluate } from 'fhirpath';
import { CPTCodeDTO, DiagnosisDTO } from '../../types/api/chart-data/chart-data.types';
import {
  IN_HOUSE_LAB_DISPLAY_TYPES,
  IN_HOUSE_LAB_OD_NULL_OPTION_SYSTEM,
  IN_HOUSE_OBS_DEF_ID_SYSTEM,
  IN_HOUSE_TEST_CODE_SYSTEM,
  IN_HOUSE_UNIT_OF_MEASURE_SYSTEM,
  OBSERVATION_INTERPRETATION_SYSTEM,
  OD_DISPLAY_CONFIG,
  OD_VALUE_VALIDATION_CONFIG,
  REFLEX_ARTIFACT_DISPLAY,
  REFLEX_TEST_ALERT_URL,
  REFLEX_TEST_CONDITION_LANGUAGES,
  REFLEX_TEST_CONDITION_URL,
  REFLEX_TEST_LOGIC_URL,
  REFLEX_TEST_TO_RUN_NAME_URL,
  REFLEX_TEST_TO_RUN_URL,
  REFLEX_TEST_TRIGGERED_URL,
  REPEAT_TEST_CPT_CODE_MODIFIER,
  REPEAT_TEST_ORDER_DETAIL_TAG_CONFIG,
  REPEATABLE_TEXT_EXTENSION_CONFIG,
} from '../../types/data/in-house/in-house.constants';
import {
  CodeableConceptDataEntryComponent,
  DataEntryComponent,
  DataEntryComponentType,
  DataEntryTestItem,
  LabComponentValueSetConfig,
  ObservationCode,
  QuantityDataEntryComponent,
  StringDataEntryComponent,
  TestComponentResult,
  Validation,
} from '../../types/data/in-house/in-house.types';
import { CODE_SYSTEM_CPT, CODE_SYSTEM_CPT_MODIFIER, EXTENSION_URL_CPT_MODIFIER } from '../rcm/constants';

export const extractAbnormalValueSetValues = (
  obsDef: ObservationDefinition,
  containedResources: (ObservationDefinition | ValueSet)[]
): LabComponentValueSetConfig[] => {
  const abnormalValueSetRef = obsDef.abnormalCodedValueSet?.reference?.substring(1);
  const abnormalValueSet = containedResources.find(
    (res) => res.resourceType === 'ValueSet' && res.id === abnormalValueSetRef
  ) as ValueSet | undefined;
  const abnormalValues = abnormalValueSet ? extractValueSetValues(abnormalValueSet) : [];
  return abnormalValues;
};

const extractValueSetValues = (valueSet: ValueSet): LabComponentValueSetConfig[] => {
  if (!valueSet.compose?.include?.[0]?.concept) {
    return [];
  }

  return valueSet.compose.include[0].concept
    .map((concept) => (concept as LabComponentValueSetConfig) || '')
    .filter(Boolean);
};

export const extractQuantityRange = (
  obsDef: ObservationDefinition
): {
  unit: string;
  normalRange: {
    low: number;
    high: number;
    unit: string;
    precision?: number;
  };
} => {
  const unit = obsDef.quantitativeDetails?.unit?.coding?.[0]?.code || '';
  const low = obsDef.qualifiedInterval?.[0]?.range?.low?.value;
  const high = obsDef.qualifiedInterval?.[0]?.range?.high?.value;
  const precision = obsDef.quantitativeDetails?.decimalPrecision;

  if (low === undefined || high === undefined) {
    throw Error('qualifiedInterval range is missing details'); // todo make this error better
  }

  return {
    unit,
    normalRange: {
      low,
      high,
      unit,
      precision,
    },
  };
};

const extractDisplayType = (obsDef: ObservationDefinition, obsName: string): IN_HOUSE_LAB_DISPLAY_TYPES => {
  const ext = obsDef.extension;
  const display = ext?.find((e) => e.url === OD_DISPLAY_CONFIG.url)?.valueString;
  if (!display) throw Error(`no display type set for this observation definition: ${obsName}`);

  if (
    display !== OD_DISPLAY_CONFIG.valueString.radio &&
    display !== OD_DISPLAY_CONFIG.valueString.select &&
    display !== OD_DISPLAY_CONFIG.valueString.numeric &&
    display !== OD_DISPLAY_CONFIG.valueString.freeText
  )
    throw Error(
      `unknown display cast to this observation definition: ${obsName} (display should be one of the follow ${Object.values(
        OD_DISPLAY_CONFIG.valueString
      )} and received: ${display})`
    );
  return display;
};

const extractNullOption = (
  obsDef: ObservationDefinition
):
  | {
      text: string;
      code: string;
    }
  | undefined => {
  const ext = obsDef.extension;
  const nullOptionCode = ext?.find((e) => e.url === IN_HOUSE_LAB_OD_NULL_OPTION_SYSTEM)?.valueCode;
  const nullOptionText = ext?.find((e) => e.url === IN_HOUSE_LAB_OD_NULL_OPTION_SYSTEM)?.valueString;
  if (nullOptionCode && nullOptionText) {
    return {
      text: nullOptionText,
      code: nullOptionCode,
    };
  }
  return;
};

const extractValidations = (obsDef: ObservationDefinition): Validation | undefined => {
  const validationExtensions = obsDef.extension?.filter((ext) => ext.url === OD_VALUE_VALIDATION_CONFIG.url);
  if (!validationExtensions || !validationExtensions.length) return undefined;

  // labs todo: in the future if we need additional validation types, we should grab them here
  const formatValidation = validationExtensions.find(
    (ext) => ext.valueCoding?.system === OD_VALUE_VALIDATION_CONFIG.formatValidation.url
  );
  if (!formatValidation || !formatValidation.valueCoding?.code) return undefined;

  const format = { value: formatValidation.valueCoding.code, display: formatValidation.valueCoding.display };

  return format ? { format } : undefined;
};

const processObservationDefinition = (
  obsDef: ObservationDefinition,
  containedResources: (ObservationDefinition | ValueSet)[],
  observation?: Observation
): DataEntryComponent | undefined => {
  const componentName = obsDef.code?.text || '';
  const observationDefinitionId = obsDef.id || '';
  const dataType = obsDef.permittedDataType?.[0] as 'Quantity' | 'CodeableConcept' | 'string';
  const loincCode =
    obsDef.code?.coding?.filter((coding) => coding.system === 'http://loinc.org').map((coding) => coding.code || '') ||
    [];

  if (dataType === 'CodeableConcept') {
    const validValueSetRef = obsDef.validCodedValueSet?.reference?.substring(1); // Remove the '#'

    const validValueSet = containedResources.find(
      (res) => res.resourceType === 'ValueSet' && res.id === validValueSetRef
    ) as ValueSet | undefined;

    const valueSet = validValueSet ? extractValueSetValues(validValueSet) : [];
    console.log('valueSet check', valueSet);
    const abnormalValues = extractAbnormalValueSetValues(obsDef, containedResources);

    const refRangeValueSet = containedResources.find(
      (res) => res.resourceType === 'ValueSet' && res.id === obsDef.normalCodedValueSet?.reference?.substring(1)
    ) as ValueSet | undefined;

    const referenceRangeValues = refRangeValueSet ? extractValueSetValues(refRangeValueSet) : [];

    const unit =
      obsDef.quantitativeDetails?.unit?.coding?.find((coding) => coding.system === IN_HOUSE_UNIT_OF_MEASURE_SYSTEM)
        ?.code ?? undefined;

    const displayType = extractDisplayType(obsDef, componentName);
    if (displayType === 'Numeric' || displayType === 'Free Text')
      throw Error(
        'Observation definition is flagged as Numeric or Free Text, currently we are only configured to support Select or Radio for CodeableConcept observation definitions '
      );

    const nullOption = extractNullOption(obsDef);

    const result = getResult(observation, dataType);
    const component: CodeableConceptDataEntryComponent = {
      componentName,
      observationDefinitionId,
      loincCode,
      dataType,
      valueSet,
      abnormalValues,
      referenceRangeValues,
      unit,
      displayType,
      nullOption,
      result,
    };
    return component;
  } else if (dataType === 'Quantity') {
    const quantityInfo = extractQuantityRange(obsDef);
    const displayType = extractDisplayType(obsDef, componentName);
    if (displayType !== 'Numeric') {
      throw Error('Quantity type observation definition is misconfigured, should be Numeric');
    }
    const result = getResult(observation, dataType);

    const nullOption = extractNullOption(obsDef);

    const component: QuantityDataEntryComponent = {
      componentName,
      observationDefinitionId,
      loincCode,
      dataType,
      unit: quantityInfo.unit,
      normalRange: quantityInfo.normalRange,
      displayType,
      result,
      nullOption,
    };
    return component;
  } else if (dataType === 'string') {
    const displayType = extractDisplayType(obsDef, componentName);
    if (displayType !== 'Free Text') {
      throw Error('String type observation definition is misconfigured, display type should be Free Text');
    }
    const validations = extractValidations(obsDef);
    const result = getResult(observation, dataType);
    const component: StringDataEntryComponent = {
      componentName,
      loincCode,
      observationDefinitionId,
      dataType,
      displayType,
      validations,
      result,
    };
    return component;
  }

  console.log('Invalid data type');
  return;
};

export function quantityRangeFormat(quantity: QuantityDataEntryComponent): string {
  return `${quantity.normalRange.low} - ${quantity.normalRange.high}`;
}

export function categoricalRangeFormat(referenceRangeValues: LabComponentValueSetConfig[] | undefined): string {
  return (
    referenceRangeValues
      ?.map((refRange) => {
        return refRange.display.charAt(0).toUpperCase() + refRange.display.slice(1);
      })
      .join(', ') ?? ''
  );
}

export const convertActivityDefinitionToDataEntryTestItem = (
  activityDef: ActivityDefinition,
  observations?: Observation[],
  serviceRequest?: ServiceRequest,
  diagnosticReport?: DiagnosticReport
): DataEntryTestItem => {
  const name = activityDef.name || '';

  const repeatable = !!activityDef?.extension?.find((ext) => ext.url === REPEATABLE_TEXT_EXTENSION_CONFIG.url);

  const methods: {
    manual?: { device: string };
    analyzer?: { device: string };
    machine?: { device: string };
  } = {};

  activityDef.participant?.[0]?.role?.coding?.forEach((coding) => {
    const methodType = coding.code || '';
    const deviceName = coding.display || '';

    if (methodType) {
      methods[methodType as keyof typeof methods] = { device: deviceName };
    }
  });

  const containedResources = activityDef.contained || [];

  const obsDefRefs = activityDef.observationRequirement || [];

  if (obsDefRefs.length === 0) {
    throw Error('No observation definitions found');
  }

  const observationMap: { [obsDefId: string]: Observation } = {};
  if (observations) {
    observations.forEach((obs) => {
      const observationIsBasedOnSr = serviceRequest ? resourceIsBasedOnServiceRequest(obs, serviceRequest) : true;
      if (observationIsBasedOnSr) {
        const obsDefIdFromExt = obs.extension?.find((ext) => ext.url === IN_HOUSE_OBS_DEF_ID_SYSTEM)?.valueString;
        if (obsDefIdFromExt) observationMap[obsDefIdFromExt] = obs;
      }
    });
  }

  const groupedComponents: DataEntryComponent[] = [];
  const radioComponents: CodeableConceptDataEntryComponent[] = [];
  for (const ref of obsDefRefs) {
    const obsDefId = ref.reference?.substring(1);
    const obsDef = containedResources.find(
      (res) => res.resourceType === 'ObservationDefinition' && res.id === obsDefId
    ) as ObservationDefinition | undefined;

    if (obsDef && obsDef.id) {
      const resultObs = observationMap[obsDef.id];
      const componentInfo = processObservationDefinition(
        obsDef,
        containedResources as (ObservationDefinition | ValueSet)[],
        resultObs
      );
      if (componentInfo) {
        if (
          componentInfo.displayType === 'Select' ||
          componentInfo.displayType === 'Numeric' ||
          componentInfo.displayType === 'Free Text'
        )
          groupedComponents.push(componentInfo);
        if (componentInfo.displayType === 'Radio') radioComponents.push(componentInfo);
      }
    }
  }

  if (!activityDef.url || !activityDef.version) {
    throw new Error(
      `ActivityDefinition is misconfigured and is missing either its url or version property: ${activityDef.url}, ${activityDef.version}. AD id is ${activityDef.id}`
    );
  }

  let reflexAlert: DataEntryTestItem['reflexAlert'];
  // validate service request and diagnostic report are indeed related
  const srIsRelatedToDr =
    diagnosticReport && serviceRequest ? resourceIsBasedOnServiceRequest(diagnosticReport, serviceRequest) : false;
  if (srIsRelatedToDr) {
    reflexAlert = checkDiagnosticReportForReflexAlert(diagnosticReport);
  }

  const orderedAsRepeat = serviceRequest
    ? !!serviceRequest.meta?.tag?.some(
        (t) =>
          t.system === REPEAT_TEST_ORDER_DETAIL_TAG_CONFIG.system && t.code === REPEAT_TEST_ORDER_DETAIL_TAG_CONFIG.code
      )
    : false;

  const cptCodes = makeCptCodeDTOsFromActivityDefinition(activityDef, orderedAsRepeat);

  const isReflexTest = activityDefinitionIsReflexTest(activityDef);

  const orderMode: DataEntryTestItem['orderMode'] = (() => {
    if (orderedAsRepeat) return 'repeat';
    if (isReflexTest) return 'reflex';
    return 'standard';
  })();

  const determineComponentsType = (): DataEntryComponentType => {
    if (groupedComponents.length && !radioComponents.length) {
      return {
        type: 'grouped',
        components: groupedComponents,
      };
    } else if (radioComponents.length && !groupedComponents.length) {
      return {
        type: 'radio',
        components: radioComponents,
      };
    } else if (radioComponents.length && groupedComponents.length) {
      console.error(
        `ActivityDefinition/${
          activityDef.id
        } returned both groupedComponents and radioComponents. Grouped: ${JSON.stringify(
          groupedComponents
        )} \n\n Radio: ${JSON.stringify(radioComponents)}`
      );
      throw new Error(
        'Could not convert ActivityDefinition to DataEntryTestItem -- received both grouped and radio components'
      );
    } else {
      return {
        type: 'empty',
        components: undefined,
      };
    }
  };

  const testItem: DataEntryTestItem = {
    name,
    methods,
    repeatable,
    orderMode,
    method: Object.keys(methods).join(' or '),
    device: Object.values(methods)
      .map((m) => m.device)
      .join(' or '),
    cptCode: cptCodes,
    components: determineComponentsType(),
    reflexAlert,
    adUrl: activityDef.url,
    adVersion: activityDef.version,
    adId: activityDef.id ?? 'unknown',
  };

  console.log('successfully converted activity ActivityDefinition to testItem format for', testItem.name);
  return testItem;
};

const makeCptCodeDTOsFromActivityDefinition = (
  activityDefinition: ActivityDefinition,
  isRepeat: boolean
): CPTCodeDTO[] => {
  const cptCodeDTOs: CPTCodeDTO[] = [];

  const cptCodeCodings = activityDefinition.code?.coding?.filter((coding) => coding.system === CODE_SYSTEM_CPT);

  cptCodeCodings?.forEach((coding) => {
    const modifierExts = coding.extension?.filter((ext) => ext.url === EXTENSION_URL_CPT_MODIFIER);
    const modifiers = configModifiersArrayFromExtension(modifierExts);

    const dto: CPTCodeDTO = {
      code: coding.code || 'unknown',
      display: activityDefinition.name || 'unknown',
      modifier: modifiers,
    };

    cptCodeDTOs.push(dto);
  });

  if (isRepeat) {
    cptCodeDTOs.push(REPEAT_TEST_CPT_CODE_MODIFIER);
  }

  return cptCodeDTOs;
};

const configModifiersArrayFromExtension = (
  modifierExtension: Extension[] | undefined
): { code: string; display: string }[] | undefined => {
  if (modifierExtension === undefined || modifierExtension.length === 0) return;

  const modifiers: { code: string; display: string }[] = [];
  modifierExtension.forEach((ext) => {
    ext.valueCodeableConcept?.coding?.forEach((c) => {
      if (c.system === CODE_SYSTEM_CPT_MODIFIER && typeof c.code === 'string') {
        const mod = { code: c.code, display: c.display ?? '' };
        modifiers.push(mod);
      }
    });
  });

  if (modifiers.length === 0) return;

  return modifiers;
};

export const resourceIsBasedOnServiceRequest = (
  resource: Observation | DiagnosticReport,
  serviceRequest: ServiceRequest
): boolean => {
  return !!resource.basedOn?.some((basedOn) => basedOn.reference === `ServiceRequest/${serviceRequest.id}`);
};

const getResult = (
  observation: Observation | undefined,
  dataType: 'CodeableConcept' | 'Quantity' | 'string'
): TestComponentResult | undefined => {
  if (!observation) return;
  let result: TestComponentResult | undefined;
  let entry: string | undefined;

  const interpretationCoding = observation.interpretation?.find(
    (i) => i?.coding?.find((c) => c.system === OBSERVATION_INTERPRETATION_SYSTEM)
  )?.coding;
  let interpretationCode: ObservationCode | undefined;
  if (interpretationCoding) {
    interpretationCode = interpretationCoding.find((c) => c.system === OBSERVATION_INTERPRETATION_SYSTEM)
      ?.code as ObservationCode;
  }

  if (dataType === 'CodeableConcept' || dataType === 'string') {
    entry = observation.valueString;
  } else {
    const entryValue = observation?.valueQuantity?.value;
    if (entryValue !== undefined) {
      entry = entryValue.toString();
    } else if (interpretationCode === 'IND') {
      // handles indeterminate option
      entry = observation.valueString;
    }
  }
  if (entry !== undefined && interpretationCode) {
    result = {
      entry,
      interpretationCode,
    };
  }
  return result;
};

// todo labs i suspect this function will need some tweaking if we add more types of conditions/expressions
export const checkIfReflexIsTriggered = (
  activityDefinition: ActivityDefinition,
  observation: Observation | undefined
): Extension | undefined => {
  if (!observation) return;
  console.log(`checking if observation triggers reflex test: ${JSON.stringify(observation)}`);

  const reflexLogicExtensions = checkActivityDefinitionForReflexLogic(activityDefinition);
  if (!reflexLogicExtensions) return;

  const {
    reflexTestToRunCanonicalUrlExt,
    reflexTestToRunNameExt,
    reflexAlertExt,
    reflexConditionLanguage,
    reflexConditionExpression,
  } = reflexLogicExtensions;

  if (reflexConditionLanguage === REFLEX_TEST_CONDITION_LANGUAGES.fhirPath) {
    const result = evaluate(observation, reflexConditionExpression, { resource: observation });
    if (result && Array.isArray(result) && result.some((res) => res === true)) {
      const reflexTestTriggeredExt: Extension = {
        url: REFLEX_TEST_TRIGGERED_URL,
        extension: [reflexTestToRunCanonicalUrlExt, reflexTestToRunNameExt, reflexAlertExt],
      };
      return reflexTestTriggeredExt;
    } else {
      console.log('result returned:', JSON.stringify(result));
      return;
    }
  } else {
    throw new Error(
      `Reflex test logic has been added for a condition language we do not currently support. Language parsed: ${reflexConditionLanguage}. Languages supported: ${JSON.stringify(
        REFLEX_TEST_CONDITION_LANGUAGES
      )}. Offending ActivityDefinition: ${activityDefinition.id}.`
    );
  }
};

export const checkActivityDefinitionForReflexLogic = (
  activityDefinition: ActivityDefinition
):
  | {
      reflexTestToRunCanonicalUrlExt: Extension;
      reflexTestToRunNameExt: Extension;
      reflexAlertExt: Extension;
      reflexConditionLanguage: string;
      reflexConditionExpression: string;
    }
  | undefined => {
  const reflexLogic = activityDefinition.extension?.find((ext) => ext.url === REFLEX_TEST_LOGIC_URL)?.extension;
  if (!reflexLogic) return;

  const reflexCondition = reflexLogic.find((ext) => ext.url === REFLEX_TEST_CONDITION_URL)?.valueExpression;
  if (!reflexCondition) {
    throw new Error(
      `Reflex logic is misconfigured. Condition valueExpression is missing. ActivityDefinition/${activityDefinition.id}`
    );
  }

  console.log(`Evaluating reflex logic for ActivityDefinition/${activityDefinition.id}`);

  // todo labs, could reduce iterations here by using reduce
  const reflexTestToRunCanonicalUrlExt = reflexLogic.find((ext) => ext.url === REFLEX_TEST_TO_RUN_URL);
  const reflexTestToRunNameExt = reflexLogic.find((ext) => ext.url === REFLEX_TEST_TO_RUN_NAME_URL);
  const reflexAlertExt = reflexLogic.find((ext) => ext.url === REFLEX_TEST_ALERT_URL);
  const reflexConditionLanguage = reflexCondition.language;
  const reflexConditionExpression = reflexCondition.expression;

  if (
    !reflexConditionLanguage ||
    !reflexConditionExpression ||
    !reflexAlertExt ||
    !reflexTestToRunNameExt ||
    !reflexTestToRunCanonicalUrlExt
  ) {
    throw new Error(
      `Reflex logic is misconfigured. One of the following is missing: reflexConditionLanguage, reflexConditionExpression, reflexAlertExt, reflexTestToRunCanonicalUrlExt, reflexTestToRunNameExt on ActivityDefinition/${activityDefinition.id}`
    );
  }

  return {
    reflexTestToRunCanonicalUrlExt,
    reflexTestToRunNameExt,
    reflexAlertExt,
    reflexConditionLanguage,
    reflexConditionExpression,
  };
};

export const checkDiagnosticReportForReflexAlert = (
  diagnosticReport: DiagnosticReport | undefined
): DataEntryTestItem['reflexAlert'] | undefined => {
  if (!diagnosticReport) return;
  const reflexLogic = diagnosticReport.extension?.find((ext) => ext.url === REFLEX_TEST_TRIGGERED_URL)?.extension;
  if (!reflexLogic) return;

  console.log(`Evaluating reflex logic for DiagnosticReport/${diagnosticReport.id}`);

  const reflexTestToRunName = reflexLogic.find((ext) => ext.url === REFLEX_TEST_TO_RUN_NAME_URL)?.valueString;
  const reflexAlert = reflexLogic.find((ext) => ext.url === REFLEX_TEST_ALERT_URL)?.valueString;
  const reflexCanonicalUrl = reflexLogic.find((ext) => ext.url === REFLEX_TEST_TO_RUN_URL)?.valueCanonical;

  if (!reflexAlert || !reflexTestToRunName || !reflexCanonicalUrl) {
    throw new Error(
      `Reflex test information on diagnostic report is misconfigured. reflexAlert or reflexTestToRunName is missing on DiagnosticReport/${diagnosticReport.id}`
    );
  }

  return { testName: reflexTestToRunName, alert: reflexAlert, canonicalUrl: reflexCanonicalUrl };
};

export const getFormattedDiagnoses = (diagnoses: DiagnosisDTO[]): string => {
  return diagnoses.map((d) => `${d.code} ${d.display}`).join(', ');
};

export const activityDefinitionIsReflexTest = (activityDefinition: ActivityDefinition): boolean => {
  const isReflex = !!activityDefinition.relatedArtifact?.some((artifact) => {
    const isDependent = artifact.type === 'depends-on';
    const isRelatedViaReflex = artifact.display === REFLEX_ARTIFACT_DISPLAY;

    return isDependent && isRelatedViaReflex;
  });

  return isReflex;
};

export const repeatTestErrorMessage = (testName: string): string => {
  return `You cannot run '${testName}' as repeat. No initial tests could be found for this encounter. Ask an admin if the version of the test you are trying to run has changed, or re-run the test not as repeat.`;
};

export const isInHouseLabServiceRequest = (resource: ServiceRequest): boolean =>
  !!resource.code?.coding?.find((c) => c.system === IN_HOUSE_TEST_CODE_SYSTEM);
